import React, { useEffect, useState } from 'react';
import { Shield, User, Clock, Search, RefreshCw, Edit3, Trash2, PenTool, Plus } from 'lucide-react';
import api from '../api/client';

const ACTION_STYLES = {
  create: { icon: Plus, fg: '#0F766E', bg: '#E6FBF7', label: 'Création' },
  update: { icon: Edit3, fg: '#B45309', bg: '#FEF3E7', label: 'Modification' },
  delete: { icon: Trash2, fg: '#B91C1C', bg: '#FEF2F2', label: 'Suppression' },
  sign: { icon: PenTool, fg: '#4F46E5', bg: '#EEF2FF', label: 'Signature' },
};

export default function Audit() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('Tous');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/audit-logs?limit=200');
      setItems(data);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((x) => {
    const okFilter = filter === 'Tous' || x.action === filter;
    const hay = `${x.user_name || ''} ${x.resource_type || ''} ${x.resource_id || ''} ${x.action || ''}`.toLowerCase();
    return okFilter && hay.includes(search.toLowerCase());
  });

  const formatWhen = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5">
      <div className="soft-card p-5 flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#EEF2FF] flex items-center justify-center">
          <Shield className="w-6 h-6 text-[#4F46E5]" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-[17px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Journal d'audit</h2>
          <p className="text-[13px] text-[#6B6C85]">Toutes les actions sensibles effectuées sur votre organisation (création, modification, suppression, signature).</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="audit-refresh" onClick={load} className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE]">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input data-testid="audit-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par utilisateur, ressource, type…" className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['Tous', 'create', 'update', 'delete', 'sign'].map((a) => (
            <button key={a} onClick={() => setFilter(a)} className={`h-10 px-3.5 rounded-xl text-[12.5px] font-semibold transition-colors ${
              filter === a ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-white border border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'
            }`}>
              {a === 'Tous' ? 'Toutes' : (ACTION_STYLES[a]?.label || a)}
            </button>
          ))}
        </div>
      </div>

      <div className="soft-card overflow-hidden">
        {loading && <div className="p-8 text-center text-[13px] text-[#8E8FA5]">Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center">
            <Shield className="w-10 h-10 text-[#D4D5E0] mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-[#4A4B63]">Aucun événement</div>
            <div className="text-[12.5px] text-[#8E8FA5]">Les actions s'afficheront ici au fur et à mesure.</div>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="divide-y divide-[#F3F4F8]">
            {filtered.map((x) => {
              const cfg = ACTION_STYLES[x.action] || { icon: Shield, fg: '#6B6C85', bg: '#F3F4F8', label: x.action };
              const Icon = cfg.icon;
              return (
                <div key={x.id} data-testid={`audit-row-${x.id}`} className="flex items-center gap-4 p-4 hover:bg-[#FAFAFE] transition-colors">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: cfg.bg }}>
                    <Icon className="w-4 h-4" style={{ color: cfg.fg }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{x.user_name || x.user_email || '—'}</span>
                      <span className="text-[12px] font-semibold px-2 py-0.5 rounded-md" style={{ background: cfg.bg, color: cfg.fg }}>{cfg.label}</span>
                      <span className="text-[12.5px] text-[#6B6C85]">sur</span>
                      <span className="text-[12.5px] font-semibold text-[#4A4B63]">{x.resource_type}</span>
                      <code className="text-[11.5px] font-mono px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#4A4B63]">{x.resource_id}</code>
                    </div>
                    {x.metadata && Object.keys(x.metadata).length > 0 && (
                      <div className="text-[11.5px] text-[#8E8FA5] mt-1 truncate">
                        {x.metadata.signatory && <span>signataire: <strong>{x.metadata.signatory}</strong> · </span>}
                        {x.metadata.fields && <span>champs: {x.metadata.fields.slice(0, 4).join(', ')}</span>}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-[11.5px] text-[#8E8FA5] shrink-0">
                    <div className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatWhen(x.created_at || x.at)}</div>
                    {x.ip && <div className="font-mono mt-0.5">IP {x.ip.slice(0, 22)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
